import { Badge, Button, Card, CardContent, SimplePagination } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Calendar, Download, HelpCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { usePayrollListQuery } from "../api/queries";
import { PAYROLL_STATUS_LABELS } from "../constants";
import { HrTour } from "../tour/HrTour";
import { payrollHistoryTourSteps } from "../tour/hrTourSteps";
import { useHrTour } from "../tour/useHrTour";

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("lo-LA", {
    style: "decimal",
    minimumFractionDigits: 0,
  }).format(n);
}

const MONTH_ABBR_LO = [
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
];

/** ສູງສຸດຕໍ່ປີ — ສຳລັບຄຳນວນຍອດລວມ / ຊ່ວງເດືອນ (ບໍ່ແມ່ນລາຍການທີ່ແບ່ງໜ້າ) */
const PAYROLL_YEAR_AGGREGATE_LIMIT = 500;

type PayrollListRow = {
  id: string;
  employeeId: string;
  periodYear: number;
  periodMonth: number;
  periodLabel: string;
  netSalary: number;
  status: string;
  paidAt: string | null;
  createdAt: string;
};

export function PayrollHistoryPage() {
  const navigate = useNavigate({ from: "/app/hr/payroll" });
  const search = useSearch({ from: "/app/hr/payroll" });
  const offset = search.offset;
  const limit = search.limit;

  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const tour = useHrTour("hr-payroll-history-tour-completed");

  const latestOverallQuery = usePayrollListQuery({ limit: 1, offset: 0 });
  const latestOverallRow = latestOverallQuery.data?.data?.[0];
  const latestYear = latestOverallRow?.periodYear ?? new Date().getFullYear();

  useEffect(() => {
    if (selectedYear != null) return;
    if (!latestOverallRow) return;
    setSelectedYear(latestOverallRow.periodYear);
  }, [latestOverallRow, selectedYear]);

  const yearArchiveQuery = usePayrollListQuery({
    year: selectedYear ?? undefined,
    limit,
    offset,
    enabled: selectedYear != null,
  });

  const yearAllQuery = usePayrollListQuery({
    year: selectedYear ?? undefined,
    limit: PAYROLL_YEAR_AGGREGATE_LIMIT,
    offset: 0,
    enabled: selectedYear != null,
  });

  const prevYearQuery = usePayrollListQuery({
    year: selectedYear != null ? selectedYear - 1 : undefined,
    limit: 1,
    offset: 0,
    enabled: selectedYear != null,
  });

  const canLoadPreviousYears = (prevYearQuery.data?.meta?.total ?? 0) > 0;

  const yearArchiveRows = yearArchiveQuery.data?.data ?? [];
  const yearAllRows = yearAllQuery.data?.data ?? [];
  const totalCount = yearArchiveQuery.data?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit) || 1);
  const currentPage = Math.min(totalPages, Math.floor(offset / limit) + 1);

  useEffect(() => {
    if (!yearArchiveQuery.isSuccess || totalCount === 0) return;
    if (offset > 0 && offset >= totalCount) {
      const nextOffset = Math.max(
        0,
        Math.floor((totalCount - 1) / limit) * limit,
      );
      navigate({
        to: "/app/hr/payroll",
        search: { offset: nextOffset, limit },
        replace: true,
      });
    }
  }, [yearArchiveQuery.isSuccess, totalCount, offset, limit, navigate]);

  const openDetail = (id: string) => {
    navigate({ to: "/app/hr/payroll/$id", params: { id } });
  };

  const paidRows = useMemo(
    () => yearAllRows.filter((r) => r.status === "paid"),
    [yearAllRows],
  );

  const totalNetPaidYtd = useMemo(() => {
    return paidRows.reduce((acc, r) => acc + r.netSalary, 0);
  }, [paidRows]);

  const fyPeriod = useMemo(() => {
    if (paidRows.length === 0) return "—";
    const months = paidRows.map((r) => r.periodMonth);
    const startMonth = Math.min(...months);
    const endMonth = Math.max(...months);
    return `${MONTH_ABBR_LO[startMonth - 1] ?? ""}-${MONTH_ABBR_LO[endMonth - 1] ?? ""}`;
  }, [paidRows]);

  const latestPaidInYear = useMemo(() => {
    let best: PayrollListRow | undefined;
    for (const r of yearAllRows) {
      if (r.status !== "paid") continue;
      if (
        !best ||
        r.periodYear > best.periodYear ||
        (r.periodYear === best.periodYear && r.periodMonth > best.periodMonth)
      ) {
        best = r;
      }
    }
    return best;
  }, [yearAllRows]);

  const latestRowAnyStatus = yearAllRows[0];
  const latestForCard = latestPaidInYear ?? latestRowAnyStatus;

  const goPage = (page: number) => {
    const nextOffset = (page - 1) * limit;
    navigate({
      to: "/app/hr/payroll",
      search: { ...search, offset: nextOffset },
    });
  };

  const resetYearNav = (year: number) => {
    setSelectedYear(year);
    navigate({ to: "/app/hr/payroll", search: { ...search, offset: 0 } });
  };

  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto w-full space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-2xl tracking-tight">
                  ປະຫວັດເງິນເດືອນ
                </h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-4" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ເບິ່ງ ແລະ ດາວໂຫລດໃບຮັບເງິນເດືອນຂອງທ່ານ
              </p>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <div>
                {selectedYear != null
                  ? `ປີງົບປະມານ ${selectedYear} — ໄລຍະເວລາ ${fyPeriod}`
                  : "ປີງົບປະມານ —"}
              </div>
            </div>
          </div>

          {selectedYear == null ? (
            <p className="text-muted-foreground">ກຳລັງໂຫລດ...</p>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-3">
                <Card className="md:col-span-2">
                  <CardContent className="p-6 py-0">
                    <p className="text-muted-foreground text-sm">ສະຫຼຸບປີ</p>
                    <p className="mt-4 font-bold text-3xl">
                      ₭ {formatCurrency(totalNetPaidYtd)}
                    </p>
                    <p className="mt-1 text-muted-foreground text-sm">
                      ຍອດສຸດທິລວມຈ່າຍແລ້ວ (ປີ)
                      {yearAllQuery.data?.meta != null &&
                      yearAllQuery.data.meta.total >
                        PAYROLL_YEAR_AGGREGATE_LIMIT ? (
                        <span className="block text-amber-600 text-xs dark:text-amber-500">
                          (ຄຳນວນຈາກ {PAYROLL_YEAR_AGGREGATE_LIMIT} ໃບລ່າສຸດເທົ່ານັ້ນ)
                        </span>
                      ) : null}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-black bg-black text-white md:col-span-1">
                  <CardContent className="p-6 py-0">
                    <p className="text-sm text-white/80">ການຊຳລະລ່າສຸດ</p>
                    <p className="mt-2 font-semibold">
                      {latestForCard?.periodLabel ?? "—"}
                    </p>
                    <p className="mt-1 text-sm text-white/80">
                      {latestForCard?.paidAt
                        ? `ຈ່າຍແລ້ວເມື່ອ ${formatDate(latestForCard.paidAt)}`
                        : "—"}
                    </p>

                    <div className="mt-4">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="bg-white text-black hover:bg-white/90"
                        disabled={
                          selectedYear == null ||
                          yearAllQuery.isFetching ||
                          yearArchiveQuery.isFetching
                        }
                        onClick={() => {
                          if (selectedYear == null) return;
                          navigate({
                            to: "/app/hr/payroll/preview",
                            search: { year: selectedYear.toString() },
                          });
                        }}
                      >
                        <Download className="mr-1 size-4" />
                        ດາວໂຫລດເອກະສານ PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div data-tourid="hr-payroll-history-cards" className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <h2 className="font-semibold">ປະຫວັດໃບຮັບເງິນເດືອນ</h2>
                  {totalCount > 0 ? (
                    <p className="text-muted-foreground text-sm">
                      ທັງໝົດ {totalCount} ໃບ · ໜ້າ {currentPage}/{totalPages}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-3">
                  {yearArchiveRows.length === 0 ? (
                    yearArchiveQuery.isFetching ? (
                      <Card>
                        <CardContent className="py-0 text-center text-muted-foreground">
                          ກຳລັງໂຫລດ...
                        </CardContent>
                      </Card>
                    ) : (
                      <Card>
                        <CardContent className="py-0 text-center text-muted-foreground">
                          ຍັງບໍ່ມີໃບຮັບເງິນເດືອນສຳລັບປີງົບປະມານ {selectedYear}
                        </CardContent>
                      </Card>
                    )
                  ) : (
                    yearArchiveRows.map((row) => (
                      <Card
                        key={row.id}
                        className="cursor-pointer transition-colors hover:bg-muted/50"
                        onClick={() => openDetail(row.id)}
                      >
                        <CardContent className="flex flex-col gap-3 p-4 py-0 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex items-center gap-3">
                            <Calendar className="size-5 text-muted-foreground" />
                            <div className="min-w-0">
                              <p className="font-medium">{row.periodLabel}</p>
                              <p className="truncate text-muted-foreground text-sm">
                                {row.paidAt
                                  ? `ຈ່າຍແລ້ວ ${formatDate(row.paidAt)}`
                                  : "—"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <div>
                              <p className="font-bold text-lg text-muted-foreground">
                                ₭ {formatCurrency(row.netSalary)}
                              </p>
                            </div>
                            <Badge
                              variant={
                                row.status === "paid"
                                  ? "default"
                                  : row.status === "confirmed"
                                    ? "secondary"
                                    : "outline"
                              }
                              className="whitespace-nowrap"
                            >
                              {PAYROLL_STATUS_LABELS[row.status] ?? row.status}
                            </Badge>

                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 min-w-8 border-black bg-black p-0 text-white hover:bg-black/90"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row.id);
                              }}
                              aria-label="ດາວໂຫລດ / ພິມ"
                            >
                              <Download className="size-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                {totalPages > 1 ? (
                  <div className="flex justify-center pt-2">
                    <SimplePagination
                      page={currentPage}
                      totalPages={totalPages}
                      onPageChange={goPage}
                    />
                  </div>
                ) : null}

                <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
                  {selectedYear != null && selectedYear !== latestYear && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-fit bg-muted/30 uppercase tracking-wide"
                      onClick={() => resetYearNav(latestYear)}
                      disabled={
                        yearArchiveQuery.isFetching || yearAllQuery.isFetching
                      }
                    >
                      {yearArchiveQuery.isFetching || yearAllQuery.isFetching
                        ? "ກຳລັງໂຫລດ..."
                        : "ກັບໄປປີປັດຈຸບັນ"}
                    </Button>
                  )}

                  {canLoadPreviousYears && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-fit bg-muted/30 uppercase tracking-wide"
                      onClick={() => {
                        if (selectedYear != null) {
                          resetYearNav(selectedYear - 1);
                        }
                      }}
                      disabled={prevYearQuery.isFetching}
                    >
                      {prevYearQuery.isFetching ? "ກຳລັງໂຫລດ..." : "ໂຫລດປີກ່ອນໜ້າ"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <HrTour
          steps={payrollHistoryTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
